import { HStack, VStack, Text, Box } from '@cad-arc';
import React, { useEffect, useRef } from 'react';
import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import { OnLexicalError } from '../../common/components/lexical/types';
import { EditorControl } from '../../common/components/lexical';
import classNames from 'classnames';
import AdditionalInfoRenderer from '../additional-info-renderer/additional-info-renderer';
import { AdditionalInfoRow } from './components/additional-info-row';
import { AdditionalInfosFilter } from './components/additional-infos-filter';
import CreateNarrativeForm from './components/create-narrative-form';
import { InlineBanners } from './components/inline-banners';
import { UnreadButton } from './components/unread-button';
import styles from './event-panel-additional-infos.module.css';
import { AdditionalInfoNotificationManager, UnreadInfo } from '../hooks/use-additional-infos/types';
import { CrossClassInformation } from './types';

type DispatchReplaceVehiclesAction = ({
    values,
    replacementGroupId,
}: {
    values: { type: 'vehicle'; text: string }[];
    replacementGroupId: string;
}) => void;

/* eslint-disable-next-line */
export interface EventPanelAdditionalInfosProps {
    agencyEventId: number;
    agencyEventNumber: string; // used to show agency event number in the error alert
    crossClassInformation?: CrossClassInformation; // causes the cross class button to appear in the narrative panel
    currentUserId: number;
    dispatchReplaceVehicles?: DispatchReplaceVehiclesAction; // this is used by additional infos to update the event panel vehicle list
    firstUnreadInfo?: UnreadInfo; // this is used by additional infos to determine where to show the unread line initially
    focusInputRef?: React.MutableRefObject<(() => void) | undefined>; // this is used by parent components to focus the narrative input
    isPanelFocused?: boolean; // this is used to determine if the unread infos in view should be marked read
    isScheduledEvent?: boolean; // this configures additional infos panel for scheduled event
    isTransmittedEvent?: boolean; // this configures additional infos panel for transmitted event
    isFirstResponder?: boolean; // this does specific configurations and changes for First Responder
    isReadOnlyMode?: boolean; // this is for a First Responder 'Narrative' tab view
    onAllRead?: (id: number) => void; // this contains a function which will be executed to let the parent know that all unreads have been seen
    onLexicalError?: OnLexicalError; // this is triggered when the lexical component throws an error, and should be used by parent to emit the error to monitoring
    onOpenEvent?: (id: number) => void; // this contains a function which allows additional infos panel to open another panel or First Responder 'Event Info' tab view
    notificationManager?: AdditionalInfoNotificationManager; // Adds compatibility for controlling event notifications from different sources.
    forceReload?: boolean;
}

export function EventPanelAdditionalInfos({
    crossClassInformation,
    currentUserId,
    dispatchReplaceVehicles,
    focusInputRef,
    isPanelFocused = false,
    isScheduledEvent = false,
    isTransmittedEvent = false,
    isFirstResponder = false,
    isReadOnlyMode = false,
    onAllRead,
    onLexicalError,
    onOpenEvent,
    forceReload,
}: EventPanelAdditionalInfosProps): React.JSX.Element {
    const canCreateAdditionalInfos = useEnabledFeatures()?.['createAdditionalInfos'] ?? true;
    const canDeleteAdditionalInfos = useEnabledFeatures()?.['deleteAdditionalInfos'] ?? true;
    const editorControlRef = useRef<EditorControl>();
    const additionalInfoRendererProps = {
        rowComponent: AdditionalInfoRow,
        addonComponent: UnreadButton,
        currentUserId,
        markVisibleAsRead: isPanelFocused,
        canDeleteInfos: canDeleteAdditionalInfos && !isReadOnlyMode,
        onOpenEvent,
        onAllRead,
        forceReload,
        isReadOnlyMode,
        isFirstResponder,
    };

    useEffect(() => {
        if (focusInputRef && editorControlRef.current) {
            focusInputRef.current = editorControlRef.current.focus;
        }
        if (isPanelFocused) {
            setTimeout(() => editorControlRef.current?.focus(), 0);
        }
    }, [editorControlRef, isPanelFocused, focusInputRef]);

    return (
        <VStack align="stretch" height="100%">
            <InlineBanners />
            {!isReadOnlyMode && (
                <Box paddingInlineStart="var(--arc-space-4)" paddingInlineEnd="4" paddingTop="2">
                    <HStack
                        justify={isFirstResponder ? 'flex-end' : 'space-between'}
                        className={classNames(
                            styles['event-panel-section-heading'],
                            !isFirstResponder && styles['event-panel-section-heading-height']
                        )}
                    >
                        {!isFirstResponder && (
                            <Text className={styles['event-panel-section-heading-text']}>
                                Narrative
                            </Text>
                        )}
                        <HStack justify="end">
                            <AdditionalInfosFilter isFirstResponder={isFirstResponder} />
                        </HStack>
                    </HStack>
                    {canCreateAdditionalInfos && !isScheduledEvent && (
                        <CreateNarrativeForm
                            editorControlRef={editorControlRef}
                            crossClassInformation={crossClassInformation}
                            onReplaceVehicles={(vehicles, narrativeId) => {
                                dispatchReplaceVehicles?.({
                                    values: vehicles.map((vehicle) => ({
                                        type: 'vehicle',
                                        text: vehicle,
                                    })),
                                    replacementGroupId: narrativeId,
                                });
                            }}
                            onLexicalError={onLexicalError}
                            isFirstResponder={isFirstResponder}
                        />
                    )}
                </Box>
            )}
            {isTransmittedEvent ? (
                <Box className={styles['event-panel-transmitted-container']}>
                    <AdditionalInfoRenderer {...additionalInfoRendererProps} />
                </Box>
            ) : (
                <AdditionalInfoRenderer {...additionalInfoRendererProps} />
            )}
        </VStack>
    );
}

export default EventPanelAdditionalInfos;
